import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, MessageCircle, Clock, Send, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  useEffect(() => {
    const scriptId = 'impetus-localbusiness-jsonld';
    if (document.getElementById(scriptId)) return;
    const s = document.createElement('script');
    s.id = scriptId;
    s.type = 'application/ld+json';
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'Impetus Investment Services',
      description: 'AMFI-registered Mutual Fund Distributor (ARN: 116347) in Chetpet, Chennai. Bridge to Financial Wellness.',
      url: window.location.origin,
      telephone: ['+91-98417-90000', '+91-73058-35581'],
      email: 'cs@impetusinvestments.in',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'New No. 28, Old No. 70, Ground Floor, Spur Tank Road, M.S. Nagar, Chetpet',
        addressLocality: 'Chennai',
        addressRegion: 'Tamil Nadu',
        postalCode: '600031',
        addressCountry: 'IN',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:30',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '09:30',
          closes: '15:00',
        },
      ],
    });
    document.head.appendChild(s);
    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We will contact you shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const whatsappNumber = '919841790000';
  const whatsappMessage = 'Hello, I would like to know more about your services.';

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="contact-heading">
            Contact Us
          </h1>
          <p className="text-base font-body text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
            We're here to help you with your investment queries. Reach out to us through any of the channels below.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-8">
                Get in Touch
              </h2>

              <div className="space-y-6">
                <div className="flex items-start bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5]" data-testid="address-card">
                  <MapPin className="w-6 h-6 text-[#D97706] mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-medium text-[#A0451E] mb-2">Office Address</h3>
                    <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
                      New No. 28, Old No. 70, Ground Floor,<br />
                      Investment Zone, Spur Tank Road,<br />
                      M.S. Nagar, Chetpet, Chennai,<br />
                      Tamil Nadu – 600031
                    </p>
                  </div>
                </div>

                <div className="flex items-start bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5]" data-testid="phone-card">
                  <Phone className="w-6 h-6 text-[#D97706] mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-medium text-[#A0451E] mb-2">Phone</h3>
                    <a href="tel:+919841790000" className="text-sm font-body text-[#4A4A4A] hover:text-[#D97706] block">
                      98417 90000
                    </a>
                    <a href="tel:+917305835581" className="text-sm font-body text-[#4A4A4A] hover:text-[#D97706] block">
                      73058 35581
                    </a>
                  </div>
                </div>

                <div className="flex items-start bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5]" data-testid="email-card">
                  <Mail className="w-6 h-6 text-[#D97706] mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-medium text-[#A0451E] mb-2">Email</h3>
                    <a href="mailto:cs@impetusinvestments.in" className="text-sm font-body text-[#4A4A4A] hover:text-[#D97706]">
                      cs@impetusinvestments.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5]" data-testid="whatsapp-card">
                  <MessageCircle className="w-6 h-6 text-[#D97706] mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-medium text-[#A0451E] mb-2">WhatsApp</h3>
                    <a
                      href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-body text-[#4A4A4A] hover:text-[#D97706]"
                    >
                      Click to chat with us
                    </a>
                  </div>
                </div>

                <div className="flex items-start bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5]" data-testid="hours-card">
                  <Clock className="w-6 h-6 text-[#D97706] mr-4 mt-1 flex-shrink-0" />
                  <div className="flex-grow">
                    <h3 className="font-heading font-medium text-[#A0451E] mb-2">Office Hours</h3>
                    <p className="text-sm font-body text-[#4A4A4A]">
                      Monday - Friday: 9:30 AM - 6:00 PM<br />
                      Saturday: 9:30 AM - 3:00 PM<br />
                      Sunday: Closed
                    </p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Impetus+Investment+Services+Chetpet+Chennai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-xs font-body font-semibold text-[#A0451E] hover:text-[#D97706] transition"
                      data-testid="google-business-link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View on Google Business Profile
                    </a>
                  </div>
                </div>
                <Link
                  to="/disclosures"
                  className="flex items-start bg-white rounded-xl p-6 border border-[#A0451E]/30 hover:border-[#A0451E] transition-colors"
                  data-testid="grievance-card"
                >
                  <ExternalLink className="w-6 h-6 text-[#D97706] mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-medium text-[#A0451E] mb-2">Investor Grievances</h3>
                    <p className="text-sm font-body text-[#4A4A4A]">
                      To raise a complaint, email cs@impetusinvestments.in. See escalation options, including SEBI SCORES and SMART ODR, on our Disclosures page.
                    </p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5]">
                <h2 className="text-2xl font-heading font-medium text-[#A0451E] mb-6">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body"
                        data-testid="contact-subject-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full btn-primary flex items-center justify-center"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="mt-16">
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-8 text-center">
              Find Us on the Map
            </h2>
            <div className="rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-lg" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.4449873974395!2d80.24116931482172!3d13.066794990780543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5266a4e8f0e0e1%3A0x1!2sChetpet%2C%20Chennai%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;